import { useEffect, useRef } from "react";

import { LogoutReason } from "@/constants/Login";
import { usePathname } from "@/crossPlatform/expo-router";
import useAuth from "@/hooks/useAuth";
import { useShouldPreventApiCallsDueToExpiredTokenSelector } from "@/store/reducers/auth";
import { isWeb } from "@/utils/platform.util";

const useHandleAuthExpiration = (): void => {
  const pathname = usePathname();
  const hasExpired = useShouldPreventApiCallsDueToExpiredTokenSelector();
  const { logout } = useAuth();
  const basePathnameRef = useRef<null | string>(null);

  useEffect(() => {
    // browser concerns only relevant to web
    if (!isWeb()) return;

    if (!hasExpired) {
      // flag cleared (e.g. after logout completes and redux resets) — reset anchor
      basePathnameRef.current = null;
      return;
    }

    // first pathname seen while expired becomes the anchor; do not fire logout on this render
    if (basePathnameRef.current === null) {
      basePathnameRef.current = pathname;
      return;
    }

    // only once user navigates somewhere, then proceed with logging them out
    // (mirrors my-equip-v1 behavior of expiration-related logout only on subsequent navigation)
    if (basePathnameRef.current === pathname) return;

    // user has navigated after being notified of AuthSessionExpiration via a toast
    // proceed to logout accordingly
    void logout(LogoutReason.AuthSessionExpiredAtApiCall);
  }, [hasExpired, pathname, logout]);
};

export default useHandleAuthExpiration;
